import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument } from "@/office/word/document";

export const lektionAbstaende: Lesson = {
  id: "abstaende",
  title: "Absatzabstand statt Leerzeilen",
  summary: "Der wohl häufigste Word-Fehler – und wie man ihn ein für alle Mal loswird.",
  durationMinutes: 9,
  learningGoals: [
    "Verstehen, warum Leerzeilen als Abstand Probleme machen",
    "Abstand vor und nach dem Absatz einstellen",
    "Abstände über die Formatvorlage steuern",
  ],
  steps: [
    {
      id: "leerzeilen-entfernen",
      title: "Warum Leerzeilen keine Abstände sind",
      explanation: [
        "Fast jeder fängt so an: Zwischen zwei Absätzen soll Luft sein, also drückt man " +
          "zweimal Enter. Das funktioniert – bis sich der Text ändert.",
        "Eine Leerzeile ist für Word ein vollwertiger Absatz. Rutscht sie ans Seitenende, " +
          "steht sie plötzlich oben auf der nächsten Seite und schiebt alles nach unten. " +
          "Der Abstand lässt sich nicht fein einstellen, sondern nur in ganzen Zeilen. " +
          "Und wenn du später den Abstand im ganzen Dokument ändern willst, musst du jede " +
          "einzelne Leerzeile von Hand anfassen.",
        "Word hat dafür eine eigene Einstellung: Abstand vor und nach dem Absatz, " +
          "einstellbar in Punkt. Du findest sie auf der Registerkarte „Layout“ in der " +
          "Gruppe „Absatz“.",
      ],
      keyPoints: [
        "Eine Leerzeile ist ein Absatz – mit allen Nebenwirkungen.",
        "Layout → Absatz → „Abstand Vor“ und „Abstand Nach“, angegeben in Punkt.",
        "Ein üblicher Wert für Fließtext sind 6 bis 12 pt nach dem Absatz.",
      ],
      task: {
        instruction:
          "Schreibe mindestens drei Absätze Fließtext ohne einzige Leerzeile dazwischen " +
          "und stelle für sie einen Abstand nach dem Absatz von mindestens 6 pt ein.",
        hint: "Alle Absätze markieren, dann unter Layout im Feld „Nach“ den Wert eintragen.",
        check: async () => {
          const snapshot = await readDocument();
          const inhalt = snapshot.filledParagraphs;

          if (inhalt.length < 3) {
            return fail(
              `Es gibt erst ${inhalt.length} ${inhalt.length === 1 ? "Absatz" : "Absätze"} mit Inhalt.`,
              "Schreibe drei kurze Absätze – zwei bis drei Sätze reichen jeweils.",
            );
          }

          const leerzeilen = snapshot.paragraphs.filter((absatz) => absatz.text.length === 0).length;
          const mitAbstand = inhalt.filter(
            (absatz) => typeof absatz.format.spaceAfter === "number" && absatz.format.spaceAfter >= 6,
          );

          return fromCriteria(
            [
              criterion(`Keine Leerzeilen im Dokument (gefunden: ${leerzeilen})`, leerzeilen === 0),
              criterion(
                `Alle Absätze haben mindestens 6 pt Abstand nach dem Absatz (erfüllt: ${mitAbstand.length} von ${inhalt.length})`,
                mitAbstand.length === inhalt.length,
              ),
            ],
            {
              success: "Sehr gut – dein Abstand hält jetzt auch, wenn sich der Text verschiebt.",
              failure: "Da ist noch etwas offen.",
              hint: "Leerzeilen findest du am schnellsten, indem du auf der Registerkarte „Start“ die Absatzmarken (¶) einblendest.",
            },
          );
        },
      },
    },
    {
      id: "abstand-in-vorlage",
      title: "Abstände gehören in die Formatvorlage",
      explanation: [
        "Jetzt verbinden sich die beiden Module: Der Absatzabstand ist eine Eigenschaft " +
          "wie Schriftgröße oder Farbe – und gehört damit genauso in die Formatvorlage.",
        "Stellst du ihn direkt am Absatz ein, musst du das bei jedem neuen Absatz " +
          "wiederholen. Steckt er in „Standard“, gilt er automatisch für jeden " +
          "Fließtextabsatz im ganzen Dokument, heute und in einem Jahr.",
        "Ein praktischer Nebeneffekt: Überschriften brauchen meist mehr Abstand nach oben " +
          "als nach unten, damit sie sichtbar zum folgenden Text gehören und nicht in der " +
          "Mitte schweben. Über die Vorlagen „Überschrift 1“ und „Überschrift 2“ ist das " +
          "einmal eingestellt und für immer erledigt.",
      ],
      keyPoints: [
        "Absatzabstand ist Teil der Formatvorlage, nicht des einzelnen Absatzes.",
        "Überschriften: mehr Abstand darüber als darunter.",
        "Im Absatzdialog (Layout → kleiner Pfeil) lässt sich alles auf einmal einstellen.",
      ],
      task: {
        instruction:
          "Übertrage deinen Absatzabstand in die Formatvorlage „Standard“: Absatz " +
          "einstellen, dann Rechtsklick auf „Standard“ im Katalog und „aktualisieren, um " +
          "der Auswahl anzupassen“. Danach müssen alle Fließtextabsätze denselben Abstand " +
          "haben.",
        hint: "Der Katalog „Formatvorlagen“ liegt auf der Registerkarte „Start“.",
        check: async () => {
          const snapshot = await readDocument();
          const standard = snapshot.filledParagraphs.filter(
            (absatz) => absatz.styleBuiltIn.toLowerCase() === "normal",
          );

          if (standard.length < 3) {
            return fail(
              `Es gibt erst ${standard.length} Absätze in der Vorlage „Standard“.`,
              "Achte darauf, dass deine Fließtextabsätze wirklich die Vorlage „Standard“ verwenden.",
            );
          }

          const werte = new Set(standard.map((absatz) => absatz.format.spaceAfter));
          const einheitlich = werte.size === 1;
          const wert = standard[0].format.spaceAfter;

          return fromCriteria(
            [
              criterion(
                `Alle „Standard“-Absätze haben denselben Abstand (gefunden: ${[...werte].join(", ")} pt)`,
                einheitlich,
              ),
              criterion(
                "Der Abstand beträgt mindestens 6 pt",
                typeof wert === "number" && wert >= 6,
              ),
            ],
            {
              success: "Der Abstand steckt jetzt in der Vorlage und gilt für jeden neuen Absatz.",
              failure: "Die Abstände sind noch nicht einheitlich.",
              hint: "Weicht ein einzelner Absatz ab, wurde er von Hand eingestellt. Markiere ihn und weise „Standard“ neu zu (Strg + Umschalt + N).",
            },
          );
        },
      },
    },
  ],
};
